import React, {LegacyRef} from "react";
import {Row, Col, Button, Form, Alert} from "react-bootstrap";
import {$t, t_key} from "../../plugins/i18n";
import {AuthService} from "../../services/auth-service";
import ChevronLeftIcon from "../icons/chevron-left-icon";
import ReCAPTCHA from "react-google-recaptcha";
import {useTranslation} from "react-i18next";
import {TwoFactorMethod} from "../../models/two_factor";
import ArrowRightIcon from "../icons/arrow-right-icon";
import TwoFactorInput from "./two-factor-input";
import ArrowCircleLeftIcon from "../icons/arrow-circle-left-icon";
import EmailIcon from "../icons/email-icon";
import SmsIcon from "../icons/sms-icon";
import ExclamationIcon from "../icons/exclamation";
import Constants from "../../Constants";

type Props = {
    tempAuthId: string;
    methods: TwoFactorMethod[];
    onCancel: (reasonKey?: string) => void;
    recaptchaSiteKey: string
};

const shouldLeaveTwoFactor = (key?: string) =>
    key === "validation.twoFA_expired"
    || key === "validation.session_expired"
    || key === "validation.unknown_application"
    || key === "validation.registration_not_configured"
    || key === "validation.patron_not_found";

const TwoFactorInline: React.FC<Props> = ({tempAuthId, methods, onCancel, recaptchaSiteKey}) => {
    const {i18n} = useTranslation();
    const recaptchaRef: LegacyRef<ReCAPTCHA> = React.createRef();

    const [chosen, setChosen] = React.useState<"sms" | "email" | null>(
        methods.find((m) => m.isEnabled)?.method ?? null
    );
    const [step, setStep] = React.useState<"pick" | "code">("pick");
    const [enteredCode, setEnteredCode] = React.useState("");
    const [error, setError] = React.useState<string | null>(null);
    const [codeInvalid, setCodeInvalid] = React.useState(false);
    const [remainingTries, setRemainingTries] = React.useState<number | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [requiresCaptcha, setRequiresCaptcha] = React.useState(false);
    const [captchaToken, setCaptchaToken] = React.useState<string | null>(null);
    const [captchaMissing, setCaptchaMissing] = React.useState(false);
    const [codeExpired, setCodeExpired] = React.useState(false);
    const backBtnRef = React.useRef<HTMLButtonElement>(null);
    const titleRef = React.useRef<HTMLHeadingElement>(null);
    const isInitialFocusDone = React.useRef(false);

    React.useEffect(() => {
        const id = window.setTimeout(() => {
            if (!isInitialFocusDone.current) {
                isInitialFocusDone.current = true;
                backBtnRef.current?.focus();
            } else {
                titleRef.current?.focus();
            }
        }, 0);
        return () => window.clearTimeout(id);
    }, [step]);

    React.useEffect(() => {
        if (!methods || methods.length === 0) {
            setError($t(t_key.login_view.tfa_no_methods));
        }
    }, [methods]);


    let pageSubtitle = "";
    if (step == "pick") {
        pageSubtitle = $t(t_key.login_view.tfa_page_subtitle_pick);
    }
    if (step == "code") {
        pageSubtitle = chosen == "email" ? $t(t_key.login_view.tfa_page_subtitle_code_prompt_email) : $t(t_key.login_view.tfa_page_subtitle_code_prompt_sms);
    }

    const isCodeFormatValid = (codeStr: string) => {
        return /^\d{6}$/.test(codeStr.trim());
    }

    const onCodeEnteredComplete = (code: string) => {
        setEnteredCode(code);
    }

    const clearCodeFeedback = () => {
        setCodeInvalid(false);
        setCodeExpired(false);
        setRemainingTries(null);
        setCaptchaMissing(false);
        setError(null);
    };

    const start = async () => {
        const chosenMethod = methods.find((m) => m.method === chosen);
        if (!chosen || !chosenMethod?.isEnabled) return;
        setLoading(true);
        clearCodeFeedback();
        setRequiresCaptcha(false);
        try {
            const resp: any = await AuthService.startSecondFactor(tempAuthId, chosen);
            if (resp?.errors) {
                const globalKey = resp.errors.global?.[0] as string | undefined;
                if (shouldLeaveTwoFactor(globalKey)) {
                    onCancel(globalKey);
                    return;
                }
                setError($t(globalKey ?? t_key.validation.login_failed));
                return;
            }

            setStep("code");
        } catch (e: any) {
            const globalKey = e?.errors?.global?.[0] as string | undefined;
            if (shouldLeaveTwoFactor(globalKey)) {
                onCancel(globalKey);
                return;
            }
            setError($t(globalKey ?? t_key.validation.login_failed));
        } finally {
            setLoading(false);
        }
    };

    const resend = () => {
        if (requiresCaptcha && !captchaToken){
            setCaptchaMissing(true);
            return;
        }
        start();
    }

    const displayErrors = (codeToVerify: string) => {
        if (!isCodeFormatValid(codeToVerify)) {
            setCodeInvalid(true);
        }
        if (requiresCaptcha && !captchaToken){
            setCaptchaMissing(true);
        }
    }

    const verify = async (codeToVerify: string) => {
        if (step == "code" && (loading || !isCodeFormatValid(codeToVerify) || (requiresCaptcha && !captchaToken))) {
            displayErrors(codeToVerify)
            return;
        }

        if (recaptchaRef?.current != null) {
            recaptchaRef.current.reset();
        }

        const trimmedCode = codeToVerify.trim();
        if (!trimmedCode) {
            setError($t(t_key.validation.required_field));
            return;
        }
        setLoading(true);
        clearCodeFeedback();
        setEnteredCode("");

        try {
            const resp: any = await AuthService.verifySecondFactor(tempAuthId, trimmedCode, captchaToken);
            if (resp?.errors) {
                const globalKey = resp.errors.global?.[0] as string | undefined;
                const triesStr = resp.errors.remaining_tries?.[0];
                if (triesStr != null) setRemainingTries(parseInt(triesStr, 10));

                if (globalKey === "validation.too_many_attempts") {
                    setRequiresCaptcha(true);
                    setCodeExpired(false);
                    return;
                }

                setRequiresCaptcha(false);

                if (globalKey === "validation.invalid_code") {
                    setCodeInvalid(true);
                    setCodeExpired(false);
                    return;
                }
                if (globalKey === "validation.code_expired") {
                    setCodeExpired(true);
                    setCodeInvalid(false);
                    setRemainingTries(null);
                    return;
                }
                if (globalKey === "validation.tfa_missing_captcha"
                    || globalKey === "validation.invalid_captcha"
                    || globalKey === "login_view.code_send_failed") {
                    setError($t(globalKey));
                    return;
                }
                if (shouldLeaveTwoFactor(globalKey)) {
                    onCancel(globalKey);
                    return;
                }
            }
        } catch (ex: any) {
            const body = ex?.response?.data ?? ex;
            const globalKey = body?.errors?.global?.[0];
            if (shouldLeaveTwoFactor(globalKey)) {
                onCancel(globalKey);
                return;
            }
            setError($t(globalKey ?? t_key.validation.login_failed));
        } finally {
            setLoading(false);
        }
    };

    const GetMethodIcon = (method: string): JSX.Element | null => {
        if (method === "email") return <EmailIcon />;
        if (method === "sms") return <SmsIcon />;
        return null;
    }

    const isHighContrast =
        typeof document !== "undefined" &&
        document.body.classList.contains("high-contrast");

    return (
        <div id={Constants.MAIN_CONTENT}>
            <Row className="mb-3">
                <Col className="col1 text-break">
                    <div className="back-bttn">
                        <Button ref={backBtnRef} variant="link" onClick={() => onCancel()}>
                            <ChevronLeftIcon/> <span className="back-bttn">{$t(t_key.buttons.back)}</span>
                        </Button>
                    </div>
                </Col>
            </Row>

            <div className="twofa-body">
                <Row className="mb-3">
                    <Col className="mx-auto text-center">
                        <h1 ref={titleRef} tabIndex={-1} className="tfa-title">{$t(t_key.login_view.tfa_page_title)}</h1>
                    </Col>
                </Row>

                {error && (
                    <Row className="mb-3">
                        <Col className="mx-auto">
                            <Alert
                                variant="danger"
                                className="tfa-error"
                                dismissible
                                onClose={() => setError(null)}
                                role="alert"
                            >
                                <div style={{ height: "1rem" }} />
                                <div className="tfa-error-content">
                                    <ExclamationIcon className="tfa-error-icon" aria-hidden="true" />
                                    <span className="tfa-error-text">{error}</span>
                                </div>
                                <div style={{ height: "1rem" }} />
                            </Alert>
                        </Col>
                    </Row>
                )}

                <Row className="mb-5">
                    <Col className="mx-auto text-center">
                        <p className="tfa-subtitle">{pageSubtitle}</p>
                    </Col>
                </Row>

                {step === "pick" && (
                    <>
                        <Row className="g-4 row-cols-1 align-items-stretch" role="radiogroup">
                            {methods.map((m) => {
                                const id = `tfa-${m.method}`;
                                const title = m.method === "email"
                                    ? $t(t_key.login_view.tfa_via_email)
                                    : $t(t_key.login_view.tfa_via_sms);
                                const description = !m.isEnabled && m.reasonKey
                                    ? $t(m.reasonKey)
                                    : `${$t(m.method === "email"
                                        ? t_key.login_view.tfa_via_email_caption
                                        : t_key.login_view.tfa_via_sms_caption)} ${m.userDetails}`;
                                const labelText = `${title}. ${description}`;

                                const blockUnavailableSelection = (e: React.SyntheticEvent) => {
                                    if (!m.isEnabled) e.preventDefault();
                                };

                                    return (
                                        <Form.Label
                                            key={id}
                                            htmlFor={id}
                                            className={`tfa-method-option ${chosen === m.method ? "selected" : ""} ${m.isEnabled ? "cursor-pointer" : "disabled"}
                                                p-3 border rounded-3 d-flex justify-content-between align-items-start`}
                                            onClick={blockUnavailableSelection}
                                        >
                                            <div className="me-3 flex-shrink-0 tfa-icon align-self-start" aria-hidden="true">
                                                {GetMethodIcon(m.method)}
                                            </div>

                                            <div className="flex-grow-1" aria-hidden="true">
                                                <div className="fw-semibold mb-2">
                                                    {title}
                                                </div>
                                                <div className="text-muted lh-base">
                                                    {description}
                                                </div>
                                            </div>

                                            <Form.Check
                                                id={id}
                                                type="radio"
                                                name="tfa-method"
                                                value={m.method}
                                                checked={chosen === m.method}
                                                aria-disabled={!m.isEnabled}
                                                onClick={blockUnavailableSelection}
                                                onKeyDown={(e) => {
                                                    if (!m.isEnabled && (e.key === " " || e.key === "Enter")) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                onChange={() => {
                                                    if (m.isEnabled) setChosen(m.method);
                                                }}
                                                className="tfa-radio ms-0 me-3 flex-shrink-0 align-self-start mt-1"
                                                aria-label={labelText}
                                            />
                                        </Form.Label>
                                    );
                                }
                            )}
                        </Row>

                        <div className="tfa-actions mx-auto mt-5">
                            <Row className="mt-4 justify-content-center justify-content-lg-end ">
                                <Col
                                    xs={12}
                                    md={12}
                                    lg="auto"
                                    className="d-flex order-1 order-lg-2 justify-content-center justify-content-lg-start"
                                >
                                    <Button
                                        variant="primary tfa"
                                        onClick={start}
                                        disabled={!chosen || loading || methods.find((m) => m.method === chosen)?.isEnabled === false}
                                        className="btn-tfa-primary w-100 w-lg-auto"
                                    >
                                        <span>{$t(t_key.buttons.tfa_next)}</span> <ArrowRightIcon />
                                    </Button>
                                </Col>
                                <Col
                                    xs={12}
                                    md={12}
                                    lg="auto"
                                    className="d-flex order-2 order-lg-1 mb-2 mb-lg-0 justify-content-center justify-content-lg-start"
                                >
                                    <span className="link link-tfa fw-medium span-link mt-4 mt-lg-0" onClick={() => onCancel()} tabIndex={0} role="link">
                                        {$t(t_key.buttons.tfa_choose_another_auth_option)}
                                    </span>
                                </Col>
                            </Row>
                        </div>
                    </>
                )}

                {step === "code" && (
                    <>
                        <Row className="justify-content-center">
                            <Col className="d-flex justify-content-center">
                                <div className="tfa-stack">
                                    <TwoFactorInput
                                        formId="tfa-form"
                                        onCodeEnteredComplete={(fullCode) => {
                                            onCodeEnteredComplete(fullCode);
                                        }}
                                        onCodeSubmitted={(fullCode) => {
                                            verify(fullCode);
                                        }}
                                        onCodeEdit={() => {
                                            setEnteredCode("");
                                            if (codeInvalid || codeExpired || remainingTries != null) {
                                                setCodeInvalid(false);
                                                setCodeExpired(false);
                                                setRemainingTries(null);
                                            }
                                        }}
                                        clearCodeInput={codeInvalid || requiresCaptcha || codeExpired}
                                    />

                                    {codeInvalid && remainingTries != null && (<span role="alert" className="visually-hidden">
                                      {[
                                          $t(t_key.validation.invalid_code),
                                          $t(t_key.login_view.tfa_page_remaining_tries) + " : " + remainingTries,
                                      ].filter(Boolean).join(". ")}
                                    </span>)}

                                    {codeInvalid && (
                                        <div className="tfa-invalid-code mt-3 mb-2 w-100" role="alert" aria-hidden="true">
                                            {$t(t_key.validation.invalid_code)}
                                        </div>
                                    )}

                                    {remainingTries != null && (
                                        <div className="tfa-remaining-tries w-100" role="alert" aria-hidden="true">
                                            {$t(t_key.login_view.tfa_page_remaining_tries) + " : " + remainingTries}
                                        </div>
                                    )}

                                    {requiresCaptcha && (
                                        <div className="tfa-invalid-code mt-3 mb-2 w-100" role="alert">
                                            {$t(t_key.validation.too_many_attempts)}
                                        </div>
                                    )}

                                    {codeExpired && (
                                        <div className="tfa-invalid-code mt-3 mb-2 w-100" role="alert">
                                            {$t(t_key.validation.code_expired)}
                                        </div>
                                    )}

                                    {requiresCaptcha && (
                                        <div className="re-captcha-cont mt-5">
                                            <ReCAPTCHA
                                                ref={recaptchaRef}
                                                onChange={(token) => setCaptchaToken(token)}
                                                sitekey={recaptchaSiteKey}
                                                hl={i18n.language}
                                                theme={isHighContrast ? "dark" : "light"}
                                            />
                                        </div>
                                    )}

                                    {captchaMissing && (
                                        <div className="tfa-invalid-code mb-5 w-100" role="alert">
                                            {$t(t_key.validation.tfa_missing_captcha)}
                                        </div>
                                    )}

                                    <div className="d-grid gap-3 w-100 my-5">
                                        <Button
                                            variant="primary"
                                            onClick={(e) => {
                                                verify(enteredCode);
                                                (e.currentTarget as HTMLButtonElement).blur();
                                            }}
                                            disabled={loading || !isCodeFormatValid(enteredCode)}
                                            className="btn-tfa-primary w-100"
                                        >
                                            <span>{$t(t_key.buttons.tfa_next)}</span> <ArrowRightIcon />
                                        </Button>


                                        <Button
                                            variant="secondary"
                                            onClick={resend}
                                            disabled={loading}
                                            className="btn-tfa-secondary w-100 mb-4"
                                        >
                                            <ArrowCircleLeftIcon/> <span className="ms-2">{$t(t_key.buttons.tfa_resend)}</span>
                                        </Button>

                                        <span className="link link-tfa fw-medium span-link mt-4 mt-lg-0" onClick={() => onCancel()} tabIndex={0} role="link">
                                        {$t(t_key.buttons.tfa_choose_another_auth_option)}
                                        </span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </>
                )}
            </div>
        </div>
    );
};

export default TwoFactorInline;
